import { useUpdateCompanyInboundSettingsMutation } from '@/modules/companies/mutations/useUpdateCompanyInboundSettingsMutation';
import { useCompanyInboundSettingsQuery } from '@/modules/companies/queries/useCompanyInboundSettingsQuery';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import Loader from '@/modules/ui/components/loader/Loader';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { CareersPageSettings } from './CareersPageSettings';

interface CareersPageSettingsSectionProps {
  companyId: string;
}

export function CareersPageSettingsSection({
  companyId,
}: CareersPageSettingsSectionProps) {
  const settingsQuery = useCompanyInboundSettingsQuery(companyId);
  const updateMutation = useUpdateCompanyInboundSettingsMutation();
  const { isSaved, markSaved, clearSaved } = useSavedFlash();

  if (settingsQuery.isLoading) return <Loader />;

  if (!settingsQuery.data) {
    return (
      <InlineError>
        {actionErrorMessage(
          'load the Careers Page settings',
          settingsQuery.error,
        )}
      </InlineError>
    );
  }

  const handleToggle = (isCareersPageEnabled: boolean) => {
    clearSaved();
    updateMutation.mutate(
      { companyId, isCareersPageEnabled },
      { onSuccess: markSaved },
    );
  };

  const handleRetry = () => {
    if (updateMutation.variables) {
      handleToggle(updateMutation.variables.isCareersPageEnabled);
    }
  };

  return (
    <CareersPageSettings
      settings={settingsQuery.data}
      isPending={updateMutation.isPending}
      isSaved={isSaved}
      error={
        updateMutation.isError
          ? actionErrorMessage(
              'update the Careers Page setting',
              updateMutation.error,
            )
          : null
      }
      onToggle={handleToggle}
      onRetry={handleRetry}
    />
  );
}
